import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import NumericInput from "react-numeric-input";

const AreaSettingsBufferHysteresis = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { data } = props;

  const jwt = localStorage.getItem("JWT");
  const link = localStorage.getItem("link");

  const types = useMemo(
    () => JSON.parse(localStorage.getItem("lotTypes") || "[]"),
    [],
  );

  const rights = JSON.parse(localStorage.getItem("rights") || "[]");
  const canEditBuffer = rights.includes("Area.SetBuffer");
  const canEditHysteresis = rights.includes("Area.SetHysteresis");

  const [originalLT, setOriginalLT] = useState([]);
  const [editedLT, setEditedLT] = useState([]);
  const [saving, setSaving] = useState(false);

  const baseRows = useMemo(
    () =>
      types.map((t) => ({
        lotType: t.id,
        name: t.name,
        buffer: 0,
        hysteresis: 0,
      })),
    [types],
  );

  useEffect(() => {
    const values = data?.lotTypeAreaValues || [];

    const merged = baseRows.map((row) => {
      const found = values.find((v) => v.lotType === row.lotType);
      return found
        ? {
            ...row,
            buffer: found.buffer,
            hysteresis: found.hysteresis,
          }
        : row;
    });

    setOriginalLT(merged);
    setEditedLT(merged);
  }, [data, baseRows]);

  const isDirty = () =>
    originalLT.some(
      (o, i) =>
        o.buffer !== editedLT[i]?.buffer ||
        o.hysteresis !== editedLT[i]?.hysteresis,
    );

  const reset = (mode = "all") => {
    setEditedLT((prev) =>
      prev.map((row, i) => {
        const orig = originalLT[i];

        if (mode === "b") {
          return { ...row, buffer: orig.buffer };
        }

        if (mode === "h") {
          return { ...row, hysteresis: orig.hysteresis };
        }
        return orig; // full reset
      }),
    );
  };

  const updateValue = (lotTypeId, field, value) => {
    setEditedLT((prev) =>
      prev.map((row) =>
        row.lotType === lotTypeId ? { ...row, [field]: value } : row,
      ),
    );
  };

  const save = async () => {
    if (!isDirty() || saving) return;

    setSaving(true);

    try {
      const requests = [];

      for (let i = 0; i < originalLT.length; i++) {
        const o = originalLT[i];
        const e = editedLT[i];

        if (canEditBuffer && o.buffer !== e.buffer) {
          requests.push(
            axios.put(
              `${link}/api/Area/BufferLotType?elementId=${data.id}&lotTypeId=${e.lotType}&buffer=${e.buffer}`,
              {},
              { headers: { Authorization: "Bearer " + jwt } },
            ),
          );
        }

        if (canEditHysteresis && o.hysteresis !== e.hysteresis) {
          requests.push(
            axios.put(
              `${link}/api/Area/HysteresisLotType?elementId=${data.id}&lotTypeId=${e.lotType}&hysteresis=${e.hysteresis}`,
              {},
              { headers: { Authorization: "Bearer " + jwt } },
            ),
          );
        }
      }

      await Promise.all(requests);
      setOriginalLT(editedLT);
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    isDirty,
    save,
    reset,
  }));

  return (
    <div className="settings-groups">
      <div className="settings-name">
        {t("elementeAllgemein.labelPufferHysterese")}
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="flex-sb" style={{ fontSize: 10, fontWeight: "bold" }}>
          <div>{t("elementeAllgemein.typ")}</div>
          <div className="flex-sa" style={{ width: 150 }}>
            <div style={{ width: 70 }}>{t("elementeAllgemein.puffer")}</div>
            <div style={{ width: 70 }}>{t("elementeAllgemein.hysterese")}</div>
          </div>
        </div>

        {editedLT.map((row) => (
          <div key={row.lotType} className="flex-sb" style={{ marginTop: 4 }}>
            <div>{row.name}</div>
            <div className="flex-sb" style={{ width: 150 }}>
              <NumericInput
                className="input-numeric input70"
                value={row.buffer}
                min={0}
                disabled={!canEditBuffer}
                onChange={(v) => updateValue(row.lotType, "buffer", v)}
              />
              <NumericInput
                className="input-numeric input70"
                value={row.hysteresis}
                min={0}
                disabled={!canEditHysteresis}
                onChange={(v) => updateValue(row.lotType, "hysteresis", v)}
              />
            </div>
          </div>
        ))}
        <div className="flex-sb" style={{ fontSize: "10px", marginTop: "3px" }}>
          <div></div>
          <div className="flex-sb" style={{ width: "150px" }}>
            {canEditBuffer && (
              <button
                className="button"
                style={{ width: "70px", cursor: "pointer" }}
                onClick={() => reset("b")}
                disabled={!canEditBuffer}
              >
                Reset
              </button>
            )}

            {canEditHysteresis && (
              <button
                className="button"
                style={{
                  width: "70px",
                  cursor: "pointer",
                  marginLeft: !canEditBuffer ? "80px" : "0px",
                }}
                onClick={() => reset("h")}
                disabled={!canEditHysteresis}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default AreaSettingsBufferHysteresis;
